import React from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { TTone } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

type TAnimationType = 'pulse' | 'shimmer';

interface IProps {
  tone?: TTone;
  width?: '100%' | 'fit-content' | 'auto' | string;
  height?: '100%' | 'fit-content' | 'auto' | string;
  borderRadius?: string;
  type?: TAnimationType;
}

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const createDesign = (
  tone: TTone,
  type: TAnimationType,
  width?: string,
  height?: string,
  borderRadius?: string,
) => {
  const createTone = () => {
    switch (tone) {
      case 'strong':
        return { baseColor: color.GRAY_1, pulseColor: color.GRAY_2, highlightColor: color.GRAY_3 };
      case 'normal':
        return { baseColor: color.GRAY_2, pulseColor: color.GRAY_3, highlightColor: color.GRAY_4 };
      default:
        return { baseColor: color.GRAY_3, pulseColor: color.GRAY_4, highlightColor: color.GRAY_5 };
    }
  };
  return { ...createTone(), type, width, height, borderRadius };
};

const Wrapper = styled.div<{ $design: ReturnType<typeof createDesign> }>`
  width: ${({ $design }) => $design.width || '100%'};
  height: ${({ $design }) => $design.height || '25px'};
  border-radius: ${({ $design }) => $design.borderRadius ?? size.BORDER_RADIUS_1};
  ${({ $design }) => $design.type === 'shimmer'
    ? css`
        background: linear-gradient(90deg, ${$design.baseColor} 0px, ${$design.highlightColor} 50%, ${$design.baseColor} 100%);
        background-size: 200% 100%;
        animation: ${shimmer} 5s infinite linear;
      `
    : css`
        background-color: ${$design.baseColor};
        animation: ${keyframes`
          0%, 100% { background-color: ${$design.baseColor}; }
          50% { background-color: ${$design.pulseColor}; }
        `} 5s infinite ease-in-out;
      `}
`;

const Skeleton: React.FC<IProps> = ({ tone = 'light', width, height, borderRadius, type = 'shimmer' }) => {
  const design = createDesign(tone, type, width, height, borderRadius);
  return <Wrapper $design={design} />;
};

export default Skeleton;
